// "Build your walk": three questions -> personal route built on the server
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildWalk, walkOptions } from '../../api';
import { t, useLang } from '../../i18n';
import { topicLabel } from '../../topics';
import { Icon, LangSwitcher, LogoTile, RouteMap } from './Kit';
import '../../styles/vod.css';
import './screens.css';

const TIMES = [30, 45, 60, 90, 120];
const WITH = ['alone', 'couple', 'friends', 'kids'];

function Chip({ on, onClick, children }) {
  return <button type="button" className={`vodp-chip ${on ? 'on' : ''}`} onClick={onClick}>{children}</button>;
}

export default function PlanScreen() {
  const lang = useLang();
  const navigate = useNavigate();
  const [opts, setOpts] = useState(null);
  const [minutes, setMinutes] = useState(45);
  const [topics, setTopics] = useState([]);
  const [withWho, setWithWho] = useState('alone');
  const [kidsMode, setKidsMode] = useState('quest');
  const [busy, setBusy] = useState(false);
  const [walk, setWalk] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => { walkOptions().then(setOpts).catch(e => setErr(e.message)); }, []);

  const toggle = (k) => setTopics(list => list.includes(k) ? list.filter(x => x !== k) : [...list, k]);
  const wantsKidsQuest = withWho === 'kids' && kidsMode === 'quest';

  async function go() {
    setBusy(true); setErr('');
    try { setWalk(await buildWalk({ minutes, topics, kids: wantsKidsQuest })); }
    catch (e) { setErr(e.message === 'no_points' ? t('no_points') : e.message); }
    setBusy(false);
  }

  const points = walk ? walk.steps.filter(s => s.lat && s.lng).map(s => ({ lat: s.lat, lng: s.lng })) : [];
  const dedup = points.filter((p, i) => i === 0 || p.lat !== points[i - 1].lat || p.lng !== points[i - 1].lng);

  return (
    <main className="vod vod-screen">
      <div className="vod-hero veil" style={{ height: 300, backgroundImage: 'url(/auth-hero.jpg)', backgroundSize: 'cover', backgroundPosition: 'center 30%' }} />
      <div className="vod-top" style={{ minHeight: 210 }}>
        <Link className="vod-round icon back" to="/excursions" aria-label="back">{Icon.back()}</Link>
        <LogoTile size="sm" />
        <LangSwitcher />
      </div>

      <section className="vod-card" key={lang}>
        <div className="vod-accent" />
        {!walk ? (<>
          <h1 className="vod-h1">{t('plan_title')}</h1>
          <p className="vod-text" style={{ margin: '6px 0 18px' }}>{t('plan_sub')}</p>
          {opts && !opts.available && <p className="vod-err">{t('no_points')}</p>}

          <div className="vodp-q">{t('q_time')}</div>
          <div className="vodp-chips">
            {TIMES.map(m => <Chip key={m} on={minutes === m} onClick={() => setMinutes(m)}>{t('t_' + m)}</Chip>)}
          </div>

          {opts && opts.topics.length > 0 && (<>
            <div className="vodp-q">{t('q_interests')} <small>{t('q_interests_hint')}</small></div>
            <div className="vodp-chips">
              {opts.topics.map(k => <Chip key={k} on={topics.includes(k)} onClick={() => toggle(k)}>{topicLabel(k, lang)}</Chip>)}
            </div>
          </>)}

          <div className="vodp-q">{t('q_with')}</div>
          <div className="vodp-chips">
            {WITH.map(w => <Chip key={w} on={withWho === w} onClick={() => setWithWho(w)}>{t('with_' + w)}</Chip>)}
          </div>

          {withWho === 'kids' && (<>
            <div className="vodp-q">{t('q_kids')}</div>
            <div className="vodp-chips">
              <Chip on={kidsMode === 'quest'} onClick={() => setKidsMode('quest')}>🎲 {t('kids_quest')}</Chip>
              <Chip on={kidsMode === 'tour'} onClick={() => setKidsMode('tour')}>🎧 {t('regular_tour')}</Chip>
            </div>
          </>)}

          {err && <p className="vod-err" style={{ marginTop: 12 }}>{err}</p>}
          <div style={{ height: 18 }} />
          <button className="vod-btn" onClick={go} disabled={busy || (opts && !opts.available)}>
            {busy ? t('building') : t('build')}{!busy && Icon.arrow()}
          </button>
        </>) : (<>
          <h1 className="vod-h1">{t('your_walk')}</h1>
          <div className="vod-meta" style={{ marginTop: 8 }}>
            <span>{Icon.clock()}{t('walk_summary', { n: walk.stops, min: walk.total_min })}</span>
          </div>
          {walk.walk_min > 0 && <p className="vod-text" style={{ marginTop: -6 }}>{t('walk_incl', { min: walk.walk_min })}</p>}
          {walk.kids_fallback && <div className="vod-hint"><b className="ic">!</b><span>{t('kids_fallback')}</span></div>}
          {walk.too_short && <div className="vod-hint"><b className="ic">!</b><span>{t('too_short')}</span></div>}

          <ol className="vodp-list">
            {walk.steps.map((s, i) => (
              <li key={s.id}>
                <span className="n">{i + 1}</span>
                <span className="tt" dir="auto">{s.title}{s.is_key && <em> · {t('key_point')}</em>}</span>
                <span className="mm">{t('min', { n: s.minutes })}</span>
              </li>
            ))}
          </ol>

          {dedup.length > 0 && <div style={{ margin: '4px 0 16px' }}><RouteMap points={dedup} height={220} /></div>}

          <button className="vod-btn caps" onClick={() => navigate(`/walk/${walk.id}/step/1`)}>{t('start')}{Icon.arrow()}</button>
          <button className="vod-btn2" onClick={() => setWalk(null)}>{t('change_answers')}</button>
        </>)}
      </section>
    </main>
  );
}
